"use client"; // Mark as a Client Component since it handles events

import { Typography } from "@aph/ui-lib-humanx";
import React, { useRef, useState } from "react";
import { ArticleDetailInterface } from "@/components/ArticlePage/api/interface";
import ArticleComponentMapping from "@/components/ArticlePage/articleComponentMapping";
import InThisArticle from "@/components/ArticlePage/Components/InThisArticle";
import ArticleSelector from "@/components/ArticlePage/redux/selectors/articleData";
import { useSyncArticleData } from "@/components/ArticlePage/syncArticleData";
import Breadcrumbs, { BreadcrumbOptionsInterface } from "@/components/Breadcrumbs";
import routes from "@/helpers/router";
import { DeviceInfo } from "@/interface/deviceInfo";
import { useAppSelector } from "@/redux/hooks";
import NavBar from "@/components/Header/NavBar";
import { ProductInfo } from "@/components/ShoppingComponent/types/productInfo";
import { COMMON_COMPONENT_TYPES } from "@/helpers/constants";
import IconLeft from "$/icons/icon-left.svg";
import styles from "./article.module.scss";

interface ArticlePageProps {
  articleData: any;
  ua: DeviceInfo;
  articleSlug: string;
  userOnBoarded: boolean;
  productInfo?: {
    [key: number | string]: ProductInfo[];
  };
}

const ArticlePage: React.FC<ArticlePageProps> = (props) => {
  const { articleData, ua, articleSlug, userOnBoarded, productInfo } = props;

  useSyncArticleData({ ArticleData: articleData, ua, articleSlug });
  const articleInfoObject: ArticleDetailInterface = useAppSelector(ArticleSelector);

  const [isOpenLink, setIsOpenLink] = useState<boolean>(false);
  const [isShowArticleFlyBottom, setIsShowArticleFlyBottom] = useState<boolean>(false);
  const [articleSectionTitle, setArticleSectionTitle] = useState<string>("");

  // Create a ref object to store section references
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  // Create a ref callback to store references
  const setSectionRef = (id: string) => (el: HTMLElement | null) => {
    sectionRefs.current[id] = el;
  };

  const categorySlug = articleData?.ArticleCategory?.data?.attributes?.Slug;

  const generateBreadcrumbs = () => {
    const articleData = articleInfoObject?.data?.[0]?.attributes;
    const breadCrumbs: [BreadcrumbOptionsInterface] = [
      {
        label: articleData?.ArticleCategory?.data?.attributes?.CategoryName,
        url: routes.category(articleData?.ArticleCategory?.data?.attributes?.Slug),
      },
    ];

    if (articleData?.Pillar?.data?.attributes?.Name) {
      const pillarName = articleData?.Pillar?.data?.attributes?.Name;
      breadCrumbs.push({
        label: pillarName,
        url: routes.pillar(articleData?.Pillar?.data?.attributes?.Slug),
      });
    }

    if (articleData?.Cluster?.data?.attributes?.Name) {
      const clusterName = articleData?.Cluster?.data?.attributes?.Name;
      breadCrumbs.push({
        label: clusterName,
        url: routes.cluster(articleData?.Cluster?.data?.attributes?.Slug),
      });
    }

    breadCrumbs.push({
      label: articleData?.Title,
      url: routes.article(articleData?.Slug),
    });

    return breadCrumbs;
  };

  const breadCrumbsArray: [BreadcrumbOptionsInterface] = generateBreadcrumbs();

  const toggleDropdown = () => {
    setIsOpenLink(!isOpenLink);
  };

  const hideListDropdown = () => {
    setIsOpenLink(false);
  };

  const navBarInfo = articleInfoObject?.data?.[0]?.attributes?.ArticleContent?.find(
    (section: any) => section.ComponentType === COMMON_COMPONENT_TYPES.NavBar
  );

  const articleSections = articleInfoObject?.data?.[0]?.attributes?.ArticleContent.filter(
    (section: any) => section?.ComponentType !== COMMON_COMPONENT_TYPES.NavBar
  );
  const leftPanelSections = articleSections?.filter((section: any) => section?.Position === "Left");
  const rightPanelSections = articleSections?.filter(
    (section: any) => section?.Position !== "Left" && section?.Position !== "Bottom"
  );
  const fullWidthSections = articleSections?.filter((section: any) => section?.Position === "Bottom");
  const fullWidthComponentTypes = fullWidthSections?.map((section: any) => section.ComponentType);

  return (
    <>
      {ua?.isDesktop && (
        <NavBar
          navBarData={navBarInfo}
          userOnBoarded={userOnBoarded}
          userAgent={ua}
          pathname={routes.article(articleSlug)}
        />
      )}
      <div className={`breadcrumb container`}>
        <Breadcrumbs options={breadCrumbsArray} />
      </div>
      {ua?.isMobile && (
        <div className={`${styles.articleinsideSection}`}>
          <>
            {articleSections?.map((section: any, index: number) => {
              return (
                <React.Fragment key={index}>
                  <div className={`${fullWidthComponentTypes?.includes(section.ComponentType) ? `` : `container`}`}>
                    <ArticleComponentMapping
                      data={section}
                      options={{
                        hideListDropdown,
                        sectionRefs,
                        setIsShowArticleFlyBottom,
                        setArticleSectionTitle,
                        setSectionRef,
                        categorySlug,
                        productInfo,
                      }}
                    />
                  </div>
                </React.Fragment>
              );
            })}
          </>

          {isShowArticleFlyBottom && (
            <div className={styles.stickyLinksRoot}>
              <div className={styles.rootLink} onClick={toggleDropdown}>
                <label className={styles.labelText}>in this article</label>
                <Typography component="p" className={styles.title}>
                  {articleSectionTitle}
                </Typography>
              </div>
              <IconLeft height={24} width={24} className={styles.ico} />
              {isOpenLink && (
                <div className={`${styles.whenLickInBottom}`}>
                  <InThisArticle
                    {...{
                      hideListDropdown,
                      sectionRefs,
                      setIsShowArticleFlyBottom,
                      setArticleSectionTitle,
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {ua?.isDesktop && (
        <>
          <div className={`${styles.columnsContainer} container`}>
            <div className={styles.leftSection}>
              {leftPanelSections.map((section: any, index: number) => {
                return (
                  <React.Fragment key={index}>
                    <ArticleComponentMapping
                      data={section}
                      options={{
                        hideListDropdown,
                        sectionRefs,
                        setIsShowArticleFlyBottom,
                        setArticleSectionTitle,
                        setSectionRef,
                        ua,
                        categorySlug,
                      }}
                    />
                  </React.Fragment>
                );
              })}
            </div>
            <div className={styles.rightSection}>
              {rightPanelSections?.map((section: any, index: number) => {
                return (
                  <React.Fragment key={index}>
                    <ArticleComponentMapping
                      data={section}
                      options={{
                        hideListDropdown,
                        sectionRefs,
                        setIsShowArticleFlyBottom,
                        setArticleSectionTitle,
                        setSectionRef,
                        categorySlug,
                        productInfo,
                      }}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div>
            {fullWidthSections?.map((section: any, index: number) => {
              return (
                <React.Fragment key={index}>
                  <ArticleComponentMapping
                    data={section}
                    options={{
                      hideListDropdown,
                      sectionRefs,
                      setIsShowArticleFlyBottom,
                      setArticleSectionTitle,
                      setSectionRef,
                      categorySlug,
                    }}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}
    </>
  );
};

export default ArticlePage;
